/**
 * SlashInlinePicker — inline slash-mention picker used by any TipTap-backed
 * composer surface that wants `/` to insert a Corpus chip atom (bucket /
 * folder / doc).
 *
 * Wraps CorpusPickerPopover in `onPick` mode with the trigger + chip list
 * hidden. Extracted from ChatPanel.tsx so both the AI Chat surface and the
 * Planning Agent's BriefIntakeChat / Template Editor can share one copy.
 */
import { CorpusPickerPopover } from "./CorpusPickerPopover";
import type { ChatComposerEditorHandle } from "./ChatComposerEditor";
import type { ThreadScope } from "@/lib/pieceCorpusScope";

type Props = {
  workspaceId: string;
  /**
   * Optional. When present, scopes the picker to a specific thread's
   * document/folder/bucket set. Surfaces without threads (Template Editor)
   * pass `null` for workspace-wide picking.
   */
  scope?: ThreadScope | null;
  open: boolean;
  onOpenChange: (o: boolean) => void;
  disabled?: boolean;
  editorRef: React.MutableRefObject<ChatComposerEditorHandle | null>;
};

const EMPTY_SCOPE: ThreadScope = { documentIds: [], folderIds: [], buckets: [] };

export const SlashInlinePicker = ({
  workspaceId,
  scope,
  open,
  onOpenChange,
  disabled,
  editorRef,
}: Props) => {
  return (
    <CorpusPickerPopover
      workspaceId={workspaceId}
      scope={scope ?? EMPTY_SCOPE}
      onChange={() => {
        /* not used in inline mode */
      }}
      disabled={disabled}
      open={open}
      onOpenChange={(o) => {
        onOpenChange(o);
        if (!o) window.setTimeout(() => editorRef.current?.focus(), 0);
      }}
      hideTrigger
      hideChips
      onPick={(kind, id, label) => {
        const clean = label.replace(/\s+/g, " ").trim();
        editorRef.current?.insertChip({ kind, id, label: clean });
        onOpenChange(false);
        window.setTimeout(() => editorRef.current?.focus(), 0);
      }}
    />
  );
};

export default SlashInlinePicker;
